import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminMe, listPlans, listSubscriptions,renewSubscription, listVendors, getSubscriptionLocations, updatePlan } from '../api';
import AdminShell from './AdminShell';
import '../styles/AdminDashboard.css';
import '../styles/SubscriptionManagement.css';

const STATUS_META = {
  active: { label: 'Active', cls: 'active' },
  expiring_soon: { label: 'Expiring Soon', cls: 'expiring' },
  expired: { label: 'Expired', cls: 'expired' }
};

function formatCurrency(value) {
  if (value === null || value === undefined) return '—';
  const num = Number(value);
  if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(num);
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const AREA_ICONS = {
  Local: 'near_me',
  Nearby: 'location_on',
  Growth: 'trending_up',
  Professional: 'workspace_premium',
  Business: 'business_center',
  City: 'location_city'
};

const DURATION_ICONS = {
  'Monthly Plan': 'calendar_month',
  'Quarterly Plan': 'event_upcoming',
  'Six Month Plan': 'workspace_premium'
};

const DURATION_PERIOD_LABEL = {
  mo: '/mo',
  quarter: '/quarter',
  '6 mo': '/6 mo'
};

// Cursor-tracking specular shine — sets --mx/--my CSS vars on the card
// as the mouse moves over it, used by .sm-shine in SubscriptionManagement.css
function handleCardMouseMove(e) {
  const card = e.currentTarget;
  const rect = card.getBoundingClientRect();
  const mx = ((e.clientX - rect.left) / rect.width) * 100;
  const my = ((e.clientY - rect.top) / rect.height) * 100;
  card.style.setProperty('--mx', `${mx}%`);
  card.style.setProperty('--my', `${my}%`);
}

export default function SubscriptionManagement() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [locationFilter, setLocationFilter] = useState('All Locations');

  const [showModal, setShowModal] = useState(false);
const [selectedPlanId, setSelectedPlanId] = useState('');
const [editForm, setEditForm] = useState({
  name: '',
  tierLabel: '',
  price: ''
});
const [formError, setFormError] = useState('');
const [submitting, setSubmitting] = useState(false);

  const loadSubscriptions = useCallback(() => {
    setLoading(true);
    listSubscriptions({ status: statusFilter, search: searchTerm || undefined, city: locationFilter })
      .then(({ data }) => setSubscriptions(data.subscriptions))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [statusFilter, searchTerm, locationFilter]);

  const loadPlans = () => {
    listPlans().then(({ data }) => setPlans(data.plans)).catch(() => {});
  };

  const loadLocations = () => {
    getSubscriptionLocations().then(({ data }) => setLocations(data.locations)).catch(() => {});
  };

  useEffect(() => {
    getAdminMe()
      .then(({ data }) => setAdmin(data.admin))
      .catch(() => {
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');
        navigate('/login');
      });
    loadPlans();
    loadLocations();
    listVendors({}).then(({ data }) => setVendors(data.vendors)).catch(() => {});
  }, [navigate]);

  useEffect(() => {
    const t = setTimeout(loadSubscriptions, 250);
    return () => clearTimeout(t);
  }, [loadSubscriptions]);

  const openModal = () => {
  setSelectedPlanId('');
  setEditForm({
    name: '',
    tierLabel: '',
    price: ''
  });
  setFormError('');
  setShowModal(true);
};
const handlePlanSelect = (e) => {
  const id = e.target.value;
  setSelectedPlanId(id);

  const selectedPlan = plans.find(
    (plan) => String(plan.id) === String(id)
  );

  if (selectedPlan) {
    setEditForm({
      name: selectedPlan.name || '',
      tierLabel: selectedPlan.tier_label || '',
      price: selectedPlan.price ?? ''
    });
  } else {
    setEditForm({
      name: '',
      tierLabel: '',
      price: ''
    });
  }

  setFormError('');
};

  const handleSavePlan = async (e) => {
  e.preventDefault();
  setFormError('');

  if (!selectedPlanId) {
    setFormError('Please select a subscription plan.');
    return;
  }

  if (!editForm.name.trim()) {
    setFormError('Plan name is required.');
    return;
  }

  if (editForm.price === '' || Number(editForm.price) < 0) {
    setFormError('Please enter a valid price.');
    return;
  }

  setSubmitting(true);

  try {
    await updatePlan(
      selectedPlanId,
      editForm.name,
      editForm.tierLabel,
      editForm.price
    );

    setShowModal(false);
    setSelectedPlanId('');

    await loadPlans();
  } catch (err) {
    setFormError(
      err.response?.data?.message || 'Failed to update subscription plan.'
    );
  } finally {
    setSubmitting(false);
  }
};

  const handleRenew = async (sub) => {
    try {
      await renewSubscription(sub.id);
      loadSubscriptions();
      loadPlans();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to renew subscription.');
    }
  };

  const areaPlans = plans
    .filter((plan) => plan.plan_category === 'area')
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));

  const durationPlans = plans
    .filter((plan) => plan.plan_category === 'duration')
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));

  return (
    <AdminShell
      admin={admin}
      badges={{}}
      pageTitle="Subscription Management"
      searchValue={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder="Search vendors..."
    >
      <div className="subscription-management-page">
        <div className="dash-welcome-row">
          <div>
            <h2 className="dash-welcome-title">Subscription Management</h2>
            <p className="dash-welcome-subtitle">Manage vendor subscription tiers, monitor active plans, and track platform revenue.</p>
          </div>
          <div className="dash-quick-actions">
            <button className="dash-quick-btn primary" onClick={openModal}>
  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
    edit
  </span>
  Edit Subscription
</button>
          </div>
        </div>

        {/* Area-based plans (6, coverage tiers) */}
        <section className="sm-plan-section">
          <div className="sm-section-header">
            <div>
              <span className="sm-section-kicker">Coverage</span>
              <h3>Area Plans</h3>
              <p>Choose the right coverage radius for each vendor.</p>
            </div>
            <span className="sm-section-count">{areaPlans.length} plans</span>
          </div>

          <div className="sm-area-grid">
            {areaPlans.map((plan) => {
              const icon = AREA_ICONS[plan.name] || 'map';
              return (
                <article
                  key={plan.id}
                  className={`sm-area-card ${plan.is_featured ? 'is-featured' : ''}`}
                  onMouseMove={handleCardMouseMove}
                >
                  <div className="sm-shine" />
                  <div className="sm-glass-highlight" />
                  <div className="sm-area-card-head">
                    <div className="sm-area-icon">
                      <span className="material-symbols-outlined">{icon}</span>
                    </div>
                    {plan.is_featured && <span className="sm-recommended">★ Recommended</span>}
                  </div>

                  <div className="sm-plan-name-row">
                    <div>
                      <h4>{plan.name}</h4>
                      <p className="sm-coverage">{plan.tier_label}</p>
                    </div>
                  </div>

                  <div className="sm-area-price">
                    <span>₹</span>{Number(plan.price).toLocaleString('en-IN')}<small>/mo</small>
                  </div>

                  <div className="sm-auto-renew">
                    <span className="material-symbols-outlined">autorenew</span>
                    Auto-renews monthly
                  </div>

                  <div className="sm-area-stats">
                    <div>
                      <span>Subscribers</span>
                      <strong>{plan.activeSubscribers ?? 0}</strong>
                    </div>
                    <div>
                      <span>Revenue</span>
                      <strong>{formatCurrency(plan.revenue ?? 0)}</strong>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

       

        {/* Location breakdown + map placeholder (real Google Maps integration planned later) */}
        <div className="dash-location-row">
          <div className="dash-location-list-panel">
            <h3 className="dash-panel-title" style={{ fontSize: 18, marginBottom: 12 }}>Subscriptions by Location</h3>
            {locations.length === 0 ? (
              <p className="dash-empty-state" style={{ padding: '16px 0' }}>No location data yet.</p>
            ) : (
              <div className="dash-location-list">
                {locations.map((loc) => (
                  <div key={loc.city} className="dash-location-row-item">
                    <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#171a20' }}>location_on</span>
                    <span className="dash-location-name">{loc.city}</span>
                    <span className="dash-location-count">{loc.subscriberCount}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          
        </div>

        {/* Subscribers table */}
        <div className="dash-directory-panel">
          <div className="dash-directory-header">
            <h3 className="dash-panel-title">Recent Subscribers</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <select className="dash-filter-select" value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                <option>All Locations</option>
                {locations.map((loc) => (
                  <option key={loc.city} value={loc.city}>{loc.city}</option>
                ))}
              </select>
              <select className="dash-filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option>All Statuses</option>
                <option value="active">Active</option>
                <option value="expiring_soon">Expiring Soon</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>
          <div className="dash-directory-table-wrap">
            {loading ? (
              <div className="dash-empty-state">Loading subscriptions...</div>
            ) : subscriptions.length === 0 ? (
              <div className="dash-empty-state">No subscriptions match your filters.</div>
            ) : (
              <table className="dash-directory-table">
                <thead>
                  <tr>
                    <th>Vendor Name</th>
                    <th>Location</th>
                    <th>Plan Type</th>
                    <th>Amount</th>
                    <th>Start Date</th>
                    <th>Expiry Date</th>
                    <th>Status</th>
                    <th className="right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((s) => {
                    const meta = STATUS_META[s.status] || STATUS_META.active;
                    return (
                      <tr key={s.id}>
                        <td>
                          <p className="dash-exec-name">{s.vendor_name}</p>
                          <p className="dash-exec-email">Vendor ID: V-{String(s.vendor_id).padStart(4, '0')}</p>
                        </td>
                        <td>{s.vendor_city || '—'}</td>
                        <td>{s.plan_name}</td>
                        <td>{formatCurrency(s.amount)}</td>
                        <td>{formatDate(s.start_date)}</td>
                        <td>{formatDate(s.expiry_date)}</td>
                        <td>
                          <span className={`dash-sub-status-pill ${meta.cls}`}>
                            <span className="dash-status-dot" />
                            {meta.label}
                          </span>
                        </td>
                        <td className="dash-row-actions">
                          {s.status !== 'active' && (
                            <button className="dash-link-btn activate" onClick={() => handleRenew(s)}>Renew</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {showModal && (
  <div className="subscription-modal-overlay">
    <div className="subscription-modal">
      <div className="subscription-modal-header">
        <div>
          <h2>Edit Subscription Plan</h2>
          <p>Choose a plan and update its current parameters.</p>
        </div>

        <button
          type="button"
          className="subscription-modal-close"
          onClick={() => setShowModal(false)}
        >
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <form onSubmit={handleSavePlan}>
        <div className="subscription-form-group">
          <label>Select Subscription Plan</label>

          <select
            value={selectedPlanId}
            onChange={handlePlanSelect}
          >
            <option value="">Select a plan</option>

            {areaPlans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name}
              </option>
            ))}
          </select>
        </div>

        {selectedPlanId && (
          <>
            <div className="subscription-form-group">
              <label>Plan Name</label>

              <input
                type="text"
                value={editForm.name}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    name: e.target.value
                  })
                }
              />
            </div>

            <div className="subscription-form-group">
              <label>Tier Label</label>

              <input
                type="text"
                value={editForm.tierLabel}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    tierLabel: e.target.value
                  })
                }
              />
            </div>

            <div className="subscription-form-group">
              <label>Monthly Price</label>

              <input
                type="number"
                min="0"
                step="0.01"
                value={editForm.price}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    price: e.target.value
                  })
                }
              />
            </div>

            <div className="subscription-form-group">
              <label>Billing Period</label>

              <input
                type="text"
                value="Monthly"
                disabled
              />
            </div>
          </>
        )}

        {formError && (
          <div className="subscription-form-error">
            {formError}
          </div>
        )}

        <div className="subscription-modal-actions">
          <button
            type="button"
            onClick={() => setShowModal(false)}
            className="subscription-cancel-btn"
          >
            Cancel
          </button>

          <button
            type="submit"
            className="subscription-save-btn"
            disabled={submitting || !selectedPlanId}
          >
            {submitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  </div>
)}
      </div>
    </AdminShell>
  );
}